import { Suspense, lazy, useEffect, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation, useParams, type Location } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { PERMISSIONS } from '@jupi/shared'
import { useAuth } from '@jupi/ui'
import { applyUserLocale } from '@jupi/ui/i18n'
import AppShell from './components/AppShell'
import { LEGACY_REDIRECTS, visibleAreas } from './config/areas'
import BookTodayPage from './pages/BookTodayPage'
import CalendarPage from './pages/CalendarPage'
import MyBookingsPage from './pages/MyBookingsPage'
import BookingFormPage from './pages/BookingFormPage'
import MaintenancePage from './pages/MaintenancePage'
import FleetPage from './pages/FleetPage'
import AircraftPage from './pages/AircraftPage'
import AircraftMessagesPage from './pages/AircraftMessagesPage'
import AircraftDefectsPage from './pages/AircraftDefectsPage'
import FlightLogPage from './pages/flightLog/FlightLogPage'
import FlightLogFormPage from './pages/flightLog/FlightLogFormPage'
import JourneyBookPage from './pages/flightLog/JourneyBookPage'
import EventsPage from './pages/EventsPage'
import FirePatrolPage from './pages/FirePatrolPage'
import FirePatrolCrewPage from './pages/FirePatrolCrewPage'
import MembersPage from './pages/MembersPage'
import CoursesPage from './pages/school/CoursesPage'
import MyStudentsPage from './pages/school/MyStudentsPage'
import MyLessonsPage from './pages/school/MyLessonsPage'
import BookingWizardPage from './pages/BookingWizardPage'
import MyTrainingPage from './pages/school/MyTrainingPage'
import DutyLogPage from './pages/school/DutyLogPage'
import TrainingRecordPage from './pages/school/TrainingRecordPage'
import LogFlightPage from './pages/school/LogFlightPage'
import CourseDetailPage from './pages/school/CourseDetailPage'
import InstructorsPage from './pages/school/InstructorsPage'
import ManualsPage from './pages/school/ManualsPage'
import BriefingsPage from './pages/school/BriefingsPage'
import DocumentsPage from './pages/DocumentsPage'
import ExamsPage from './pages/exams/ExamsPage'
import ExamAttemptPage from './pages/exams/ExamAttemptPage'
import PlanPage from './pages/plan/PlanPage'
import PlanGpsPage from './pages/plan/GpsPage'
import PlanFuelPage from './pages/plan/FuelPage'
import PlanWeightPage from './pages/plan/WeightPage'
import PlanWorkedWeightPage from './pages/plan/WorkedWeightPage'
import PlanOfpPage from './pages/plan/OfpPage'
import PlanMapPage from './pages/plan/MapPage'
import PlanCrosswindPage from './pages/plan/CrosswindPage'
import PlanRoutesPage from './pages/plan/RoutesPage'
import PlanWaypointsPage from './pages/plan/WaypointsPage'
import PlanLinksPage from './pages/plan/LinksPage'
import PlanWeatherPage from './pages/plan/WeatherPage'
import PlanAtisPage from './pages/plan/AtisPage'
import PlanNotamsPage from './pages/plan/NotamsPage'
// Lazy: the phone input brings every country's flag and the phone-number
// metadata, which the rest of the app does not need.
// The markdown renderer and the zip reader are only needed on these two.
const BriefingReaderPage = lazy(() => import('./pages/school/BriefingReaderPage'))
const BriefingEditorPage = lazy(() => import('./pages/school/BriefingEditorPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const ExpensesPage = lazy(() => import('./pages/expenses/ExpensesPage'))
const ExpenseNewPage = lazy(() => import('./pages/expenses/ExpenseNewPage'))
const ExpenseClaimPage = lazy(() => import('./pages/expenses/ExpenseClaimPage'))
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'))
// Lazy: the charting library is big and only this page draws charts.
const StatsPage = lazy(() => import('./pages/stats/StatsPage'))
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import EmailLinkPage from './pages/EmailLinkPage'
import JoinPage from './pages/JoinPage'
import VerifyApplicationEmailPage from './pages/VerifyApplicationEmailPage'
import NotFoundPage from './pages/NotFoundPage'

export default function App() {
  const { user, loading } = useAuth()
  const location = useLocation()

  // The member's saved language preference overrides whatever the browser
  // reported, as soon as we know who they are.
  useEffect(() => {
    applyUserLocale(user?.locale)
  }, [user?.locale])

  // The public membership application form: the same page whether or not
  // anyone is signed in, and never held up waiting to find out.
  if (location.pathname === '/join') return <JoinPage />
  if (location.pathname === '/join/verify') return <VerifyApplicationEmailPage />
  // The pages an emailed link opens, and "Forgot password?": the same
  // whether or not someone is already signed in on this browser -- an
  // invite may well be opened where another member is signed in.
  if (location.pathname === '/forgot-password') return <ForgotPasswordPage />
  if (location.pathname === '/reset-password') return <ResetPasswordPage />
  if (location.pathname === '/login/email') return <EmailLinkPage />

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100dvh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Remember where they were headed, so signing in lands them there
            rather than dumping everyone on the front page. */}
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    )
  }

  // A booking form opened over a page (desktop) names that page as its
  // `background`: the routes render it, and the form is a dialog on top.
  const background = (location.state as { background?: Location } | null)?.background
  const bookingDialog = background && /^\/bookings\/(new|\d+)$/.test(location.pathname)

  return (
    <>
    <Routes location={bookingDialog ? background : location}>
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />

        <Route path="/bookings" element={<BookTodayPage />} />
        <Route path="/bookings/calendar" element={<CalendarPage />} />
        <Route path="/bookings/mine" element={<MyBookingsPage />} />
        <Route path="/bookings/new" element={<BookingFormPage />} />
        <Route path="/bookings/wizard" element={<BookingWizardPage />} />
        <Route path="/bookings/:bookingId" element={<BookingFormPage />} />

        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/fleet/maintenance" element={<MaintenancePage />} />
        <Route path="/fleet/messages" element={<AircraftMessagesPage />} />
        <Route path="/fleet/defects" element={<AircraftDefectsPage />} />
        <Route path="/fleet/logs" element={<FlightLogPage />} />
        <Route path="/fleet/logbook" element={<JourneyBookPage />} />
        <Route path="/fleet/logs/new" element={<FlightLogFormPage />} />
        <Route path="/fleet/logs/:flightId" element={<FlightLogFormPage />} />
        <Route path="/fleet/:registration" element={<AircraftPage />} />

        <Route path="/plan" element={<PlanPage />} />
        <Route path="/plan/weight" element={<PlanWeightPage />} />
        <Route path="/plan/weight/worked" element={<PlanWorkedWeightPage />} />
        <Route path="/plan/gps" element={<PlanGpsPage />} />
        <Route path="/plan/fuel" element={<PlanFuelPage />} />
        <Route path="/plan/ofp" element={<PlanOfpPage />} />
        <Route path="/plan/map" element={<PlanMapPage />} />
        <Route path="/plan/weather" element={<PlanWeatherPage />} />
        <Route path="/plan/atis" element={<PlanAtisPage />} />
        <Route path="/plan/notams" element={<PlanNotamsPage />} />
        <Route path="/plan/crosswind" element={<PlanCrosswindPage />} />
        <Route path="/plan/routes" element={<PlanRoutesPage />} />
        <Route path="/plan/waypoints" element={<PlanWaypointsPage />} />
        <Route path="/plan/links" element={<PlanLinksPage />} />

        <Route path="/school/training" element={<MyTrainingPage />} />
        <Route path="/school/training/:enrolmentId" element={<TrainingRecordPage />} />
        <Route path="/school/students" element={<MyStudentsPage />} />
        <Route path="/school/lessons" element={<MyLessonsPage />} />
        <Route path="/school/students/:enrolmentId" element={<TrainingRecordPage />} />
        <Route path="/school/students/:enrolmentId/log" element={<LogFlightPage />} />
        <Route path="/school/duty" element={<DutyLogPage />} />
        <Route path="/school/courses" element={<CoursesPage />} />
        <Route path="/school/courses/:courseCode" element={<CourseDetailPage />} />
        <Route path="/school/instructors" element={<InstructorsPage />} />
        <Route path="/school/briefings" element={<BriefingsPage />} />
        <Route path="/school/briefings/new" element={<BriefingEditorPage />} />
        <Route path="/school/briefings/:packId" element={<BriefingReaderPage />} />
        <Route path="/school/briefings/:packId/edit" element={<BriefingEditorPage />} />
        <Route path="/school/manuals" element={<ManualsPage scopes={['ato', 'dto']} titleKey="school.manuals" />} />

        <Route
          path="/fire-patrol"
          element={
            <Guard permission={PERMISSIONS.firePatrolRead}>
              <FirePatrolPage />
            </Guard>
          }
        />
        <Route
          path="/fire-patrol/crew"
          element={
            <Guard permission={PERMISSIONS.firePatrolDispatch}>
              <FirePatrolCrewPage />
            </Guard>
          }
        />

        <Route path="/club/events" element={<EventsPage />} />
        <Route path="/club/members" element={<MembersPage />} />
        <Route
          path="/club/stats"
          element={
            <Suspense fallback={null}>
              <StatsPage />
            </Suspense>
          }
        />
        <Route path="/club/documents" element={<DocumentsPage />} />
        <Route
          path="/club/exams"
          element={
            <Guard permission={PERMISSIONS.examTake}>
              <ExamsPage />
            </Guard>
          }
        />
        <Route
          path="/club/exams/attempts/:id"
          element={
            <Guard permission={PERMISSIONS.examTake}>
              <ExamAttemptPage />
            </Guard>
          }
        />
        <Route path="/club/manuals" element={<ManualsPage scopes={['generic']} titleKey="nav.clubManuals" />} />

        <Route
          path="/profile"
          element={
            <Suspense fallback={null}>
              <ProfilePage />
            </Suspense>
          }
        />
        <Route
          path="/profile/expenses"
          element={
            <Guard permission={PERMISSIONS.expenseSubmit}>
              <Suspense fallback={null}>
                <ExpensesPage />
              </Suspense>
            </Guard>
          }
        />
        <Route
          path="/profile/expenses/new"
          element={
            <Guard permission={PERMISSIONS.expenseSubmit}>
              <Suspense fallback={null}>
                <ExpenseNewPage />
              </Suspense>
            </Guard>
          }
        />
        <Route
          path="/profile/expenses/:id"
          element={
            <Guard permission={PERMISSIONS.expenseSubmit}>
              <Suspense fallback={null}>
                <ExpenseClaimPage />
              </Suspense>
            </Guard>
          }
        />
        <Route
          path="/profile/invoices"
          element={
            <Guard permission={PERMISSIONS.invoiceRead}>
              <Suspense fallback={null}>
                <InvoicesPage />
              </Suspense>
            </Guard>
          }
        />

        {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<RedirectKeepingQuery to={to} />} />
        ))}
        <Route path="/book/:bookingId" element={<LegacyBookingRedirect />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    {bookingDialog ? (
      <Routes>
        <Route path="/bookings/new" element={<BookingFormPage inDialog />} />
        <Route path="/bookings/:bookingId" element={<BookingFormPage inDialog />} />
      </Routes>
    ) : null}
    </>
  )
}

// The palolento pages show to nobody without the permission, rather than
// loading into a 403.
function Guard({ permission, children }: { permission: string; children: ReactNode }) {
  const { can } = useAuth()
  return can(permission) ? children : <NotFoundPage />
}

// The front door: the first area this member can use. Bookings for almost
// everyone; a flight school area will slot in ahead of it for students and
// instructors once it has pages.
function Home() {
  const { can } = useAuth()
  const first = visibleAreas(can)[0]
  return <Navigate to={first?.items[0]?.to ?? '/profile'} replace />
}

// The calendar's "book this slot" links carry ?registration=&start=; a
// redirect that dropped them would open an empty form.
function RedirectKeepingQuery({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

function LegacyBookingRedirect() {
  const { bookingId } = useParams()
  return <Navigate to={`/bookings/${bookingId ?? ''}`} replace />
}
